import type ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import getLastestNonDraftItemId from 'owa-mail-reading-pane-store-conversation/lib/utils/getLastestNonDraftItemId';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';
import { getListViewState, getStore as getListViewStore } from 'owa-mail-list-store';
import { openChatPane, type OpenChatPaneOptions } from 'owa-copilot-dab/lib/utils/openChatPane';
import {
    MessageAnnotationSource,
    QueryAnnotationType,
    type QueryAnnotation,
} from '@1js/sydney-types';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store-conversation/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import type { OpenBizChatSidePaneParams } from 'owa-bizchat-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isMailboxSharedOrDelegate } from 'owa-account-shared-mailbox-utils';

/**
 * Specific variation of `executePrompt` that grabs the current selected mail's queryAnnotation to send
 * to Copilot Chat along with the rest of the prompt parameters.
 */
export const executePromptWithQueryAnnotation = (
    promptText: OpenChatPaneOptions['promptText'],
    /** Also known as "CMM ID" */
    formCode: OpenBizChatSidePaneParams['formCode']
): void => {
    openChatPane({
        params: {
            formCode,
            queryAnnotations: getQueryAnnotations(),
            shouldExecuteQuery: true,
        },
        promptText,
    });
};

const getQueryAnnotations = (): QueryAnnotation[] => {
    const { selectedTableViewId } = getListViewState();
    const tableView = getListViewStore().tableViews.get(selectedTableViewId);
    const isConversationViewRP = isReadingPaneStackedView(tableView?.tableQuery.listViewType);

    const conversationReadingPaneViewState = getConversationReadingPaneViewState();
    const itemReadingPaneViewState = getItemReadingPaneViewState();

    const id = isConversationViewRP
        ? conversationReadingPaneViewState?.conversationId?.Id
        : itemReadingPaneViewState?.itemId;

    const clientItem = getClientItem(id, isConversationViewRP);
    const mailboxInfo = getModuleContextMailboxInfo();
    const mailboxInformation =
        !isFeatureEnabled('acct-enable-shared-in-bizchat') ||
        !isMailboxSharedOrDelegate(mailboxInfo)
            ? undefined
            : {
                  mailboxType: 1, // SharedMailbox
                  mailboxSmtpAddress: mailboxInfo.mailboxSmtpAddress,
                  auxiliaryMailboxGuid: mailboxInfo.auxiliaryMailboxGuid ?? undefined,
              };
    const queryAnnotationType =
        isFeatureEnabled('rp-summary-for-conversations') && isConversationViewRP
            ? QueryAnnotationType.EmailConversation
            : QueryAnnotationType.Message;

    return [
        {
            id: isFeatureEnabled('rp-summary-for-conversations')
                ? id
                : clientItem?.ItemId?.Id || '',
            messageAnnotationSource: MessageAnnotationSource.ClientAnnotated,
            queryAnnotationType,
            text: clientItem?.Subject || '',
            messageAnnotationMetadata: {
                '@type': queryAnnotationType,
                mailboxInformation,
            },
        },
    ];
};

const getClientItem = (id: string, isConversationViewRP: boolean): ClientItem | undefined => {
    const { items, conversations } = getMailStore();
    const conversationId = conversations.get(id)?.conversationId.Id;
    const useLatestItemId = isConversationViewRP && conversationId;

    return items.get(useLatestItemId ? getLastestNonDraftItemId(conversationId) : id);
};
